import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {
  CopilotSpace,
  CopilotSpaceGitHubFileResource,
  CopilotSpaceGitHubIssueResource,
  CopilotSpaceGitHubPullRequestResource,
  CopilotSpaceRepositoryResource,
  CopilotSpaceResource,
} from '@github-ui/copilot-chat-types'

type RepositoryBackedResource =
  | CopilotSpaceRepositoryResource
  | CopilotSpaceGitHubFileResource
  | CopilotSpaceGitHubIssueResource
  | CopilotSpaceGitHubPullRequestResource

type SpaceOwnership = Pick<CopilotSpace, 'owner' | 'ownerIsOrg' | 'allowsPrivateReposFromOtherOrgs'>

/**
 * The owner whose private repositories a space may use as sources, or undefined when the space is not
 * restricted. Organizations opted into `copilot_spaces_allow_repos_from_other_orgs` keep unrestricted sources.
 */
export function getSpaceSourceScopeOwner(space: SpaceOwnership): string | undefined {
  if (!copilotFeatureFlags.spacesOrgScoping) return undefined
  if (space.ownerIsOrg && space.allowsPrivateReposFromOtherOrgs) return undefined
  return space.owner
}

/** Public repositories are always allowed; private ones must belong to the scope owner. */
export function isRepoInSpaceScope(
  repo: {isPrivate: boolean; ownerLogin: string | undefined},
  scopeOwner: string | undefined,
): boolean {
  if (!scopeOwner) return true
  return !repo.isPrivate || repo.ownerLogin?.toLowerCase() === scopeOwner.toLowerCase()
}

/** Sources backed by private repositories the space owner does not own, which do not work in chat. */
export function getOutOfScopeResources(
  space: SpaceOwnership & Pick<CopilotSpace, 'resources'>,
): RepositoryBackedResource[] {
  const scopeOwner = getSpaceSourceScopeOwner(space)
  if (!scopeOwner) return []
  return space.resources.filter(
    (resource): resource is RepositoryBackedResource =>
      !resource.markedForDestroy &&
      isPrivateRepositoryBackedResource(resource) &&
      !isRepoInSpaceScope({isPrivate: true, ownerLogin: resource.ownerLogin}, scopeOwner),
  )
}

function isPrivateRepositoryBackedResource(resource: CopilotSpaceResource): resource is RepositoryBackedResource {
  return 'private' in resource && resource.private === true
}
